import {
  ESocialNetwork,
  type ISocialNetworkConfig,
} from '@mahawi/eshop-common/src/types';
import { Button, Form, Input, Select, Space, Switch } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import SettingsTable from '../components/social-networks/settings-table';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  socialNetworksLoad,
  socialNetworksSetEnabled,
  socialNetworksUpsert,
} from '../reducers/social-networks/reducer';
import { SPACING } from '../theme/tokens';

const SOCIAL_NETWORK_OPTIONS: { label: string; value: ESocialNetwork }[] = [
  { label: 'Facebook', value: ESocialNetwork.FACEBOOK },
  { label: 'Instagram', value: ESocialNetwork.INSTAGRAM },
];

const DEFAULT_NAMES: Record<ESocialNetwork, string> = {
  [ESocialNetwork.FACEBOOK]: 'Facebook',
  [ESocialNetwork.INSTAGRAM]: 'Instagram',
  [ESocialNetwork.TWITTER]: 'Twitter',
  [ESocialNetwork.LINKEDIN]: 'LinkedIn',
  [ESocialNetwork.PINTEREST]: 'Pinterest',
  [ESocialNetwork.TIKTOK]: 'TikTok',
  [ESocialNetwork.YOUTUBE]: 'YouTube',
};

function SocialNetworksContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const socialNetworksConfigs = useSelector(
    (s: RootState) => s.SocialNetworks.configs,
  );
  const socialNetworksInProcess = useSelector(
    (s: RootState) => s.SocialNetworks.inProcess,
  );

  const [selectedNetwork, setSelectedNetwork] = useState<
    ESocialNetwork | undefined
  >(undefined);
  const [networkName, setNetworkName] = useState<string>('');
  const [networkEnabled, setNetworkEnabled] = useState<boolean>(true);

  useEffect((): void => {
    dispatch(socialNetworksLoad());
  }, [dispatch]);

  const existingNetworks: Set<ESocialNetwork> = new Set(
    socialNetworksConfigs.map(
      (c: ISocialNetworkConfig): ESocialNetwork => c.socialNetwork,
    ),
  );

  const availableOptions: { label: string; value: ESocialNetwork }[] =
    SOCIAL_NETWORK_OPTIONS.filter(
      (opt): boolean => !existingNetworks.has(opt.value),
    );

  const handleNetworkChange = useCallback((value: ESocialNetwork): void => {
    setSelectedNetwork(value);
    setNetworkName(DEFAULT_NAMES[value] ?? '');
  }, []);

  const handleAdd = useCallback((): void => {
    if (selectedNetwork === undefined || !networkName.trim()) {
      return;
    }
    dispatch(
      socialNetworksUpsert(selectedNetwork, networkName.trim(), networkEnabled),
    );
    setSelectedNetwork(undefined);
    setNetworkName('');
    setNetworkEnabled(true);
  }, [selectedNetwork, networkName, networkEnabled, dispatch]);

  const handleToggle = useCallback(
    (socialNetwork: ESocialNetwork, isEnabled: boolean): void => {
      dispatch(socialNetworksSetEnabled(socialNetwork, isEnabled));
    },
    [dispatch],
  );

  return (
    <Space orientation="vertical" size={SPACING.xl} style={{ width: '100%' }}>
      <DetailPageHeader title="Social network settings" />

      <SettingsTable
        configs={socialNetworksConfigs}
        inProcess={socialNetworksInProcess}
        onToggle={handleToggle}
      />

      {availableOptions.length > 0 && (
        <Space align="end" wrap>
          <Form.Item label="Network" style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select network"
              style={{ width: 180 }}
              value={selectedNetwork}
              onChange={handleNetworkChange}
              options={availableOptions}
            />
          </Form.Item>

          <Form.Item label="Name" style={{ marginBottom: 0 }}>
            <Input
              value={networkName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setNetworkName(e.target.value)
              }
              style={{ width: 180 }}
              placeholder="Display name"
            />
          </Form.Item>

          <Form.Item label="Enabled" style={{ marginBottom: 0 }}>
            <Switch
              checked={networkEnabled}
              onChange={(checked: boolean): void => setNetworkEnabled(checked)}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              disabled={
                selectedNetwork === undefined ||
                !networkName.trim() ||
                socialNetworksInProcess
              }
              loading={socialNetworksInProcess}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Form.Item>
        </Space>
      )}
    </Space>
  );
}

export default SocialNetworksContainer;
